import { DatePicker, InputNumber, Select, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";

const { Text } = Typography;

export interface DateRangeValue {
    startDateType: 'absolute' | 'relative';
    startDateAbsolute: string;
    startDateOffset: number;
    endDateType: 'absolute' | 'relative';
    endDateAbsolute: string;
    endDateOffset: number;
}

interface DateRangeFieldProps {
    value?: DateRangeValue;
    onChange?: (value: DateRangeValue) => void;
    disabled?: boolean;
}

const DEFAULT_VALUE: DateRangeValue = {
    startDateType: 'absolute',
    startDateAbsolute: '',
    startDateOffset: -7,
    endDateType: 'absolute',
    endDateAbsolute: '',
    endDateOffset: 0,
};

export const DateRangeField = ({ value = DEFAULT_VALUE, onChange, disabled }: DateRangeFieldProps) => {
    const patch = (update: Partial<DateRangeValue>) => onChange?.({ ...value, ...update });

    return (
        <Space direction="vertical" size={8}>
            {(["start", "end"] as const).map((edge) => {
                const typeKey = edge === "start" ? "startDateType" : "endDateType";
                const absKey = edge === "start" ? "startDateAbsolute" : "endDateAbsolute";
                const offsetKey = edge === "start" ? "startDateOffset" : "endDateOffset";
                const label = edge === "start" ? "From" : "To";

                return (
                    <Space key={edge} size={8} align="center">
                        <Text strong style={{ minWidth: 36, display: 'inline-block' }}>{label}</Text>
                        <Select
                            value={value[typeKey]}
                            onChange={(v) => patch({ [typeKey]: v })}
                            style={{ width: 110 }}
                            disabled={disabled}
                            options={[
                                { value: "relative", label: "Relative" },
                                { value: "absolute", label: "Absolute" },
                            ]}
                        />
                        {value[typeKey] === "relative" ? (
                            <>
                                <Tag color="blue" style={{ margin: 0 }}>today</Tag>
                                <InputNumber
                                    value={value[offsetKey]}
                                    onChange={(v) => patch({ [offsetKey]: v ?? 0 })}
                                    min={-365}
                                    max={365}
                                    style={{ width: 72 }}
                                    disabled={disabled}
                                />
                            </>
                        ) : (
                            <DatePicker
                                value={value[absKey] ? dayjs(value[absKey], "YYYY-MM-DD") : null}
                                onChange={(_d, ds) => patch({ [absKey]: typeof ds === "string" ? ds : "" })}
                                disabled={disabled}
                            />
                        )}
                    </Space>
                );
            })}
        </Space>
    );
};

export default DateRangeField;
